// client/context/RadioContext.jsx
// ✅ Globaler Radio-Player Context - Musik läuft seitenübergreifend
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../components/ui/AuthContext";

// Schweizer Radio-Sender - Kompakte Auswahl, HTTPS
const STATIONS = [
  // SRG DEUTSCH
  { id: "swisspop", name: "Swiss Pop", tag: "Non-Stop Hits", url: "https://stream.srg-ssr.ch/m/rsp/mp3_128", color: "#ec4899" },
  { id: "srf3", name: "SRF 3", tag: "Pop & Hits", url: "https://stream.srg-ssr.ch/m/drs3/mp3_128", color: "#ef4444" },
  { id: "srf1", name: "SRF 1", tag: "Info & Musik", url: "https://stream.srg-ssr.ch/m/drs1/mp3_128", color: "#dc2626" },
  { id: "virus", name: "SRF Virus", tag: "Urban/Hip-Hop", url: "https://stream.srg-ssr.ch/m/drsvirus/mp3_128", color: "#8b5cf6" },
  
  // ENERGY
  { id: "energybe", name: "Energy Bern", tag: "Charts", url: "https://energybern.ice.infomaniak.ch/energybern-high.mp3", color: "#f59e0b" },
  
  // CH MEDIA
  { id: "radio24", name: "Radio 24", tag: "Zürich", url: "https://stream.streambase.ch/radio24/mp3-192/direct/", color: "#3b82f6" },
  { id: "argovia", name: "Radio Argovia", tag: "Aargau", url: "https://stream.streambase.ch/argovia/mp3-192/direct/", color: "#22c55e" },
  { id: "pilatus", name: "Radio Pilatus", tag: "Zentralschweiz", url: "https://stream.streambase.ch/radiopilatus/mp3-192/chmedia/", color: "#14b8a6" },
  { id: "central", name: "Radio Central", tag: "Zentralschweiz", url: "https://stream.streambase.ch/rcentral/mp3-192/direct/", color: "#0d9488" },
  { id: "virgin", name: "Virgin Radio CH", tag: "Rock/Pop", url: "https://stream.streambase.ch/vrock/mp3-192/direct/", color: "#f43f5e" },
  
  // WEITERE SCHWEIZER (alle HTTPS verifiziert)
  { id: "fm1", name: "FM1", tag: "Ostschweiz", url: "https://stream.streambase.ch/fm1/mp3-192/direct/", color: "#e879f9" },
  { id: "radiobern1", name: "Radio Bern1", tag: "Bern", url: "https://radiobern1.ice.infomaniak.ch/radiobern1-128.mp3", color: "#c084fc" },
  { id: "capitalfm", name: "Capital FM", tag: "Bern Hits", url: "https://capitalfm.ice.infomaniak.ch/capitalfm-128.mp3", color: "#f59e0b" },
  
  // FRANZÖSISCH
  { id: "couleur3", name: "Couleur 3", tag: "Alternative FR", url: "https://stream.srg-ssr.ch/m/couleur3/mp3_128", color: "#06b6d4" },
  
  // ITALIENISCH
  { id: "retetre", name: "Rete Tre", tag: "Hits IT", url: "https://stream.srg-ssr.ch/m/retetr/mp3_128", color: "#10b981" },
];

const STORAGE_KEY = "widget_radio_prefs";

const RadioContext = createContext(null);

export function RadioProvider({ children }) {
  const { user } = useAuth();
  const audioRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [station, setStationState] = useState(STATIONS[0]);
  const [isLoading, setIsLoading] = useState(false);

  // Stop radio on logout
  useEffect(() => {
    if (!user && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      setIsPlaying(false);
      setIsLoading(false);
    }
  }, [user]);

  // Load saved preferences
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (saved.stationId) {
        const found = STATIONS.find((s) => s.id === saved.stationId);
        if (found) setStationState(found);
      }
      if (typeof saved.volume === "number") setVolumeState(saved.volume);
    } catch {}
  }, []);

  // Save preferences when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ stationId: station.id, volume }));
  }, [station.id, volume]);

  // Create audio element once (persists across page navigation)
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;

    const handlePlaying = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handleError = () => {
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("error", handleError);
      audio.pause();
      audio.src = "";
    };
  }, []);

  // Volume sync
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // Play
  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || isPlaying) return;

    setIsLoading(true);
    audio.src = station.url;
    try {
      await audio.play();
      setIsPlaying(true);
    } catch {
      setIsLoading(false);
    }
  }, [station.url, isPlaying]);

  // Pause
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.src = "";
    setIsPlaying(false);
    setIsLoading(false);
  }, []);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Change station
  const setStation = useCallback(async (newStation) => {
    setStationState(newStation);
    
    if (isPlaying && audioRef.current) {
      setIsLoading(true);
      audioRef.current.src = newStation.url;
      try {
        await audioRef.current.play();
      } catch {
        setIsPlaying(false);
      }
      setIsLoading(false);
    }
  }, [isPlaying]);

  // Set volume
  const setVolume = useCallback((v) => {
    setVolumeState(v);
    setIsMuted(false);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted((m) => !m);
  }, []);

  const value = {
    // State
    stations: STATIONS,
    station,
    isPlaying,
    isLoading,
    volume,
    isMuted,
    // Actions
    play,
    pause,
    togglePlay,
    setStation,
    setVolume,
    toggleMute,
  };

  return (
    <RadioContext.Provider value={value}>
      {children}
    </RadioContext.Provider>
  );
}

export function useRadio() {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error("useRadio must be used within RadioProvider");
  }
  return context;
}

export default RadioContext;
